import type { VoiceMessage } from '../api/types';
import type { AudioPlayerState } from '../hooks/useAudioPlayer';
import './AudioPlayer.css';

interface Props {
  voice: VoiceMessage | null;
  state: AudioPlayerState;
  onToggle: () => void;
  onSeek: (time: number) => void;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export function AudioPlayer({ voice, state, onToggle, onSeek }: Props) {
  if (!voice) return null;

  const currentTime = state.currentTime;
  const duration = state.duration || voice.duration || 0;

  return (
    <section className="audio-player" aria-label="Audio player">
      <div className="audio-player-inner">
        <button
          type="button"
          className="audio-player-toggle"
          onClick={onToggle}
          aria-label={state.isPlaying ? 'Pause audio' : 'Play audio'}
        >
          {state.isPlaying ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          )}
        </button>

        <div className="audio-player-body">
          <div className="audio-player-row">
            <strong className="audio-player-title">{voice.title}</strong>
            <span className="audio-player-time">{formatTime(currentTime)} / {formatTime(duration)}</span>
          </div>
          <input
            className="audio-player-range"
            type="range"
            min={0}
            max={duration || 0}
            value={Math.min(currentTime, duration || 0)}
            step={0.1}
            onChange={(event) => onSeek(Number(event.target.value))}
            aria-label="Seek audio playback"
          />
        </div>
      </div>
    </section>
  );
}
